import { useGetGroupedPermissionsQuery } from 'api/roles'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import { useAbility } from 'utilities/ability'
import {
  findPermissionDisplayName,
  getPermissionLabel,
  type CaslSubject,
} from 'utilities/permissionMap'

const t = buildTranslate('nav.permission_tooltip')

type RbacNavResult = {
  disabled: boolean
  disabledReason: string | undefined
}

export const useRbacNavDisabled = (subject: CaslSubject): boolean => {
  const ability = useAbility()
  const rbacEnabled = useFlag('role-based-access-control', false)
  // RBAC: 'access' is enough to click (SPEC-009)
  // Legacy: keep existing 'manage' threshold
  return rbacEnabled
    ? ability.cannot('access', subject)
    : ability.cannot('manage', subject)
}

/** Mirrors the server's `rbac_authorize_any!('manage', …)`. Returns a *disabled*
 *  boolean rather than a capability, to match `PermissionTooltip` and the
 *  `formatPermissionReason*` helpers.
 *
 *  Unlike `useRbacNavDisabled` above, which gates on 'access', this gates writes,
 *  and deliberately has no `rbacEnabled` branch — 'manage' is already the legacy
 *  threshold, so one check is correct in both modes.
 *
 *  Presentation only: never a reason to drop the server-side authorize (ORD-626). */
export const useRbacManageAnyDisabled = (subjects: CaslSubject[]): boolean => {
  const ability = useAbility()
  return !subjects.some(subject => ability.can('manage', subject))
}

export const useRbacManageDisabled = (subject: CaslSubject): boolean =>
  useRbacManageAnyDisabled([subject])

/** Single lookup implementation shared by `usePermissionResourceLabel` and
 *  `usePermissionLabel` below: resolves the label only while `enabled`, so
 *  the query is skipped (and undefined returned) whenever the caller's own
 *  gate says not to bother. */
const usePermissionLabelWhen = (
  subject: CaslSubject,
  enabled: boolean
): string | undefined => {
  const { data } = useGetGroupedPermissionsQuery(undefined, {
    skip: !enabled,
  })
  if (!enabled) return undefined
  return getPermissionLabel(subject, data?.groups)
}

/** Resolves the human-readable permission label for a CASL subject directly
 *  from the grouped-permissions payload, with NO dependency on
 *  `rbac-permission-tooltips` — that flag only controls tooltip
 *  PRESENTATION and has no business gating page body copy. Still requires
 *  `role-based-access-control`: the legacy path has no grouped-permissions
 *  data to resolve a label against. */
export const usePermissionResourceLabel = (
  subject: CaslSubject
): string | undefined => {
  const rbacEnabled = useFlag('role-based-access-control', false)
  const { data } = useGetGroupedPermissionsQuery(undefined, {
    skip: !rbacEnabled,
  })
  if (!rbacEnabled) return undefined
  // findPermissionDisplayName, NOT getPermissionLabel: the latter humanizes the
  // resource slug when the payload hasn't resolved, so body copy would read
  // `require "reporting location" access` on every first paint — and the
  // caller's curated fallback would never fire, because a truthy string came
  // back. Returning undefined until a real display_name lands is what lets the
  // caller's `?? <literal>` do its job.
  return findPermissionDisplayName(subject, data?.groups)
}

/** Resolves the human-readable permission label for a CASL subject, for
 *  TOOLTIP presentation. Returns undefined when either flag is off or the
 *  subject is unmapped. */
export const usePermissionLabel = (
  subject: CaslSubject
): string | undefined => {
  const rbacEnabled = useFlag('role-based-access-control', false)
  const tooltipsEnabled = useFlag('rbac-permission-tooltips', false)
  return usePermissionLabelWhen(subject, rbacEnabled && tooltipsEnabled)
}

/** Single source for tooltip wording. Change here to update all tooltips. */
export const formatPermissionReason = (
  label: string | undefined,
  disabled: boolean,
  suffix?: string
): string | undefined => {
  if (!disabled || !label) return undefined
  const base = t('requires_permission', { label })
  return suffix ? `${base} ${suffix}` : base
}

/** OR-variant: any one of `labels` satisfies the gate. Undefined labels
 *  (permission-tooltips flag off, unmapped subjects) are filtered out. */
export const formatPermissionReasonOr = (
  labels: Array<string | undefined>,
  disabled: boolean
): string | undefined => {
  if (!disabled) return undefined
  const resolved = labels.filter((l): l is string => Boolean(l))
  if (resolved.length === 0) return undefined
  const joined = resolved.map(l => `"${l}"`).join(', ')
  return t('requires_one_of', { labels: joined })
}

/** Convenience hook: resolves label + formats reason in one call. */
export const usePermissionReason = (
  subject: CaslSubject,
  disabled: boolean,
  suffix?: string
): string | undefined => {
  const label = usePermissionLabel(subject)
  return formatPermissionReason(label, disabled, suffix)
}

export const useRbacNavDisabledWithReason = (
  subject: CaslSubject
): RbacNavResult => {
  const ability = useAbility()
  const rbacEnabled = useFlag('role-based-access-control', false)
  const label = usePermissionLabel(subject)
  const action = rbacEnabled ? 'access' : 'manage'
  const disabled = ability.cannot(action, subject)
  const disabledReason = formatPermissionReason(label, disabled)

  return { disabled, disabledReason }
}

export const useParentNavAlwaysEnabled = (): boolean => {
  // RBAC: parent menus never disabled (SPEC-001)
  // Legacy: preserve existing disabled logic
  return useFlag('role-based-access-control', false)
}
